import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconArrowUpRight, IconMenu2, IconSearch, IconX } from '@tabler/icons-react';
import AuthGuard from 'utils/route-guard/AuthGuard';
import LogoSection from './LogoSection';
import ProfileSection from './Header/ProfileSection';
import ThemeButton from 'ui-component/ThemeButton';
import panel from 'menu-items/panel';
const items = panel.children;
export default function MainLayout() {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [command, setCommand] = useState(false);
  const [query, setQuery] = useState('');
  const current = items.find((item) => pathname === item.url) || items[0];
  const filtered = items.filter((item) => item.title.includes(query.trim()));
  const closeCommand = () => {
    setCommand(false);
    setQuery('');
  };
  useEffect(() => {
    setDrawer(false);
  }, [pathname]);
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommand((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const nav = (
    <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ height: 64, px: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <LogoSection />
        {mobile && (
          <IconButton aria-label="关闭导航" onClick={() => setDrawer(false)}>
            <IconX size={20} />
          </IconButton>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ px: 3, pt: 3, pb: 1 }}>
        工作空间
      </Typography>
      <List sx={{ px: 1.5 }} aria-label="控制台导航">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <ListItemButton
              key={item.id}
              component={Link}
              to={item.url}
              selected={current.id === item.id}
              aria-current={current.id === item.id ? 'page' : undefined}
              sx={{ mb: 0.5, minHeight: 44 }}
            >
              <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>
                <Icon size={19} stroke={1.7} />
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{ fontSize: 14, fontWeight: current.id === item.id ? 600 : 400 }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto', p: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button fullWidth component={Link} to="/" color="inherit" endIcon={<IconArrowUpRight size={16} />}>
          返回主页
        </Button>
      </Box>
    </Box>
  );
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <a className="skip-link" href="#main-content">
        跳到主要内容
      </a>
      <Drawer
        variant={mobile ? 'temporary' : 'permanent'}
        open={mobile ? drawer : true}
        onClose={() => setDrawer(false)}
        sx={{ '& .MuiDrawer-paper': { width: 228, borderRight: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' } }}
        ModalProps={{ keepMounted: true }}
      >
        {nav}
      </Drawer>
      <AppBar
        position="fixed"
        color="inherit"
        sx={{
          boxShadow: 'none',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          width: { md: 'calc(100% - 228px)' },
          ml: { md: '228px' }
        }}
      >
        <Toolbar sx={{ gap: 1, px: { xs: 2, md: 4 } }}>
          {mobile && (
            <IconButton aria-label="打开导航" onClick={() => setDrawer(true)}>
              <IconMenu2 size={22} />
            </IconButton>
          )}
          <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{current.title}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<IconSearch size={17} />}
            onClick={() => setCommand(true)}
            sx={{ color: 'text.secondary', minWidth: { xs: 40, sm: 180 }, justifyContent: 'space-between', mr: 1 }}
            aria-label="搜索页面，快捷键 Ctrl 或 Command K"
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>快速导航</Box>
            <Chip size="small" label="⌘ K" sx={{ display: { xs: 'none', sm: 'flex' }, ml: 3 }} />
          </Button>
          <ThemeButton />
          <ProfileSection />
        </Toolbar>
      </AppBar>
      <Box component="main" id="main-content" tabIndex={-1} sx={{ ml: { md: '228px' }, pt: '64px', minWidth: 0 }}>
        <Box sx={{ p: { xs: 2, sm: 3, lg: 4 }, maxWidth: 1600, mx: 'auto' }}>
          <AuthGuard>
            <Outlet />
          </AuthGuard>
        </Box>
      </Box>
      <Dialog open={command} onClose={closeCommand} fullWidth maxWidth="sm" aria-labelledby="command-title">
        <DialogTitle id="command-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          快速导航
          <IconButton aria-label="关闭快速导航" onClick={closeCommand}>
            <IconX size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="搜索页面名称…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': '搜索页面' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch size={20} />
                </InputAdornment>
              )
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && filtered[0]) {
                navigate(filtered[0].url);
                closeCommand();
              }
            }}
          />
          <List>
            {filtered.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => {
                  navigate(item.url);
                  closeCommand();
                }}
                sx={{ mt: 0.5, minHeight: 44 }}
              >
                <ListItemText primary={item.title} />
                <IconArrowUpRight size={17} />
              </ListItemButton>
            ))}
          </List>
          {!filtered.length && (
            <Typography color="text.secondary" py={3} textAlign="center">
              没有匹配的页面
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Enter 打开首个结果 · Esc 关闭
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
